import React, { useMemo, useState } from 'react';
import { Alert, Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ErrorBar,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import './RegimeChart.css';

const DEFAULT_MODEL = 'Active';
const DEFAULT_VIEW = 'regimes';

const MODEL_CONFIG = {
  Absolute: {
    axisLabel: 'Average Return (%)',
    positiveColor: '#2563eb',
    negativeColor: '#c2410c',
  },
  Active: {
    axisLabel: 'Average Active Return (%)',
    positiveColor: '#0f766e',
    negativeColor: '#b45309',
  },
};

const formatPercent = (value, digits = 2) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return 'N/A';
  }
  return `${numericValue.toFixed(digits)}%`;
};

const formatAxisPercent = (value) => formatPercent(value, 0);

const formatNumber = (value, digits = 2) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return 'N/A';
  }
  return numericValue.toFixed(digits);
};

const formatMonthYear = (rawDate) => {
  const parsedDate = new Date(rawDate);
  if (Number.isNaN(parsedDate.getTime())) {
    return 'N/A';
  }
  return parsedDate.toLocaleDateString(undefined, {
    month: 'short',
    year: 'numeric',
  });
};

const buildChartRows = (payload) => {
  const regimes = Array.isArray(payload?.regimes) ? payload.regimes : [];

  return regimes.map((regime) => {
    const averageReturn = Number(regime?.average_return);
    const ciLower = Number(regime?.confidence_interval?.lower);
    const ciUpper = Number(regime?.confidence_interval?.upper);

    const hasAverageReturn = Number.isFinite(averageReturn);
    const hasConfidenceInterval = Number.isFinite(ciLower) && Number.isFinite(ciUpper) && hasAverageReturn;

    return {
      regime: regime?.label || '',
      averageReturn: hasAverageReturn ? averageReturn * 100 : null,
      confidenceIntervalError: hasConfidenceInterval
        ? [
            (averageReturn - ciLower) * 100,
            (ciUpper - averageReturn) * 100,
          ]
        : null,
      ciLower: Number.isFinite(ciLower) ? ciLower * 100 : null,
      ciUpper: Number.isFinite(ciUpper) ? ciUpper * 100 : null,
      observationCount: Number(regime?.observation_count) || 0,
    };
  });
};

const buildScatterRows = (payload) => {
  const scatterSeries = Array.isArray(payload?.scatter_series) ? payload.scatter_series : [];

  return scatterSeries
    .map((point) => {
      const level = Number(point?.level);
      const rawReturn = Number(point?.return);

      if (!Number.isFinite(level) || !Number.isFinite(rawReturn)) {
        return null;
      }

      return {
        date: point?.date || '',
        level,
        returnValue: rawReturn * 100,
        regime: point?.regime || '',
      };
    })
    .filter(Boolean);
};

const buildRegressionSegment = (payload) => {
  const line = Array.isArray(payload?.scatter_regression?.line) ? payload.scatter_regression.line : [];
  if (line.length !== 2) {
    return null;
  }

  const [startPoint, endPoint] = line;
  const startLevel = Number(startPoint?.level);
  const startReturn = Number(startPoint?.return);
  const endLevel = Number(endPoint?.level);
  const endReturn = Number(endPoint?.return);

  if (
    !Number.isFinite(startLevel) ||
    !Number.isFinite(startReturn) ||
    !Number.isFinite(endLevel) ||
    !Number.isFinite(endReturn)
  ) {
    return null;
  }

  return [
    { x: startLevel, y: startReturn * 100 },
    { x: endLevel, y: endReturn * 100 },
  ];
};

const RegimeTooltip = ({ active, payload, confidenceLevel }) => {
  if (!active || !Array.isArray(payload) || !payload.length) {
    return null;
  }

  const point = payload[0]?.payload;
  if (!point) {
    return null;
  }

  return (
    <div className="regime-chart__tooltip">
      <div className="regime-chart__tooltip-title">{point.regime}</div>
      <p className="regime-chart__tooltip-copy">
        Avg return: {formatPercent(point.averageReturn)}
      </p>
      <p className="regime-chart__tooltip-copy">
        {Math.round((confidenceLevel || 0.95) * 100)}% CI:{' '}
        {point.ciLower === null || point.ciUpper === null
          ? 'N/A'
          : `${formatPercent(point.ciLower)} to ${formatPercent(point.ciUpper)}`}
      </p>
      <p className="regime-chart__tooltip-copy">Observations: {point.observationCount}</p>
    </div>
  );
};

const ScatterTooltip = ({ active, payload, levelLabel }) => {
  if (!active || !Array.isArray(payload) || !payload.length) {
    return null;
  }

  const point = payload[0]?.payload;
  if (!point) {
    return null;
  }

  return (
    <div className="regime-chart__tooltip">
      <div className="regime-chart__tooltip-title">{formatMonthYear(point.date)}</div>
      <p className="regime-chart__tooltip-copy">
        {levelLabel}: {formatNumber(point.level, 2)}
      </p>
      <p className="regime-chart__tooltip-copy">
        Return: {formatPercent(point.returnValue)}
      </p>
      <p className="regime-chart__tooltip-copy">Regime: {point.regime || 'N/A'}</p>
    </div>
  );
};

const RegimeBarView = ({ chartRows, config, confidenceLevel }) => (
  <div className="regime-chart__canvas">
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={chartRows} margin={{ top: 16, right: 24, left: 12, bottom: 8 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <ReferenceLine y={0} stroke="#64748b" strokeOpacity={0.8} />
        <XAxis dataKey="regime" axisLine={false} tickLine={false} />
        <YAxis
          width={72}
          axisLine={false}
          tickLine={false}
          tickFormatter={formatAxisPercent}
          label={{
            value: config.axisLabel,
            angle: -90,
            position: 'insideLeft',
            style: {
              fill: '#475467',
              fontSize: 12,
              textAnchor: 'middle',
            },
          }}
        />
        <Tooltip
          cursor={{ fill: 'rgba(15, 23, 42, 0.04)' }}
          content={<RegimeTooltip confidenceLevel={confidenceLevel} />}
        />
        <Bar dataKey="averageReturn" maxBarSize={64}>
          {chartRows.map((row) => (
            <Cell
              key={row.regime}
              fill={row.averageReturn !== null && row.averageReturn < 0 ? config.negativeColor : config.positiveColor}
            />
          ))}
          <ErrorBar dataKey="confidenceIntervalError" stroke="#0f172a" strokeWidth={1.5} width={6} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  </div>
);

const RegimeScatterView = ({ scatterRows, regressionSegment, config, levelLabel }) => (
  <div className="regime-chart__canvas">
    <ResponsiveContainer width="100%" height="100%">
      <ScatterChart margin={{ top: 16, right: 24, left: 12, bottom: 24 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <ReferenceLine y={0} stroke="#64748b" strokeOpacity={0.8} />
        {regressionSegment && (
          <ReferenceLine
            segment={regressionSegment}
            stroke="#0f172a"
            strokeWidth={2}
            strokeDasharray="6 4"
            ifOverflow="extendDomain"
          />
        )}
        <XAxis
          type="number"
          dataKey="level"
          name={levelLabel}
          axisLine={false}
          tickLine={false}
          label={{
            value: levelLabel,
            position: 'insideBottom',
            offset: -10,
            style: {
              fill: '#475467',
              fontSize: 12,
            },
          }}
        />
        <YAxis
          type="number"
          dataKey="returnValue"
          width={72}
          axisLine={false}
          tickLine={false}
          tickFormatter={formatAxisPercent}
          label={{
            value: config.axisLabel,
            angle: -90,
            position: 'insideLeft',
            style: {
              fill: '#475467',
              fontSize: 12,
              textAnchor: 'middle',
            },
          }}
        />
        <Tooltip
          cursor={{ strokeDasharray: '3 3' }}
          content={<ScatterTooltip levelLabel={levelLabel} />}
        />
        <Scatter
          data={scatterRows}
          fill={config.positiveColor}
          fillOpacity={0.72}
          stroke={config.positiveColor}
          strokeWidth={1}
        />
      </ScatterChart>
    </ResponsiveContainer>
  </div>
);

const RegimeChart = ({
  data,
  metricKey,
  levelLabel,
  subtitle,
  scatterSubtitle,
  emptyMessage,
  selectedType: controlledSelectedType,
  onTypeChange,
  selectedView: controlledSelectedView,
  onViewChange,
  middleControl = null,
}) => {
  const [internalSelectedType, setInternalSelectedType] = useState(DEFAULT_MODEL);
  const [internalSelectedView, setInternalSelectedView] = useState(DEFAULT_VIEW);
  const selectedType = controlledSelectedType ?? internalSelectedType;
  const selectedView = controlledSelectedView ?? internalSelectedView;

  const regimePayload = data?.[selectedType]?.[0]?.[metricKey] || null;
  const chartRows = useMemo(() => buildChartRows(regimePayload), [regimePayload]);
  const scatterRows = useMemo(() => buildScatterRows(regimePayload), [regimePayload]);
  const regressionSegment = useMemo(() => buildRegressionSegment(regimePayload), [regimePayload]);
  const config = MODEL_CONFIG[selectedType];
  const hasAlignedData = Number(regimePayload?.aligned_observation_count) > 0;
  const hasScatterData = scatterRows.length > 0;

  const handleTypeChange = (event, newType) => {
    void event;
    if (newType !== null) {
      if (onTypeChange) {
        onTypeChange(newType);
        return;
      }
      setInternalSelectedType(newType);
    }
  };

  const handleViewChange = (event, newView) => {
    void event;
    if (newView !== null) {
      if (onViewChange) {
        onViewChange(newView);
        return;
      }
      setInternalSelectedView(newView);
    }
  };

  const selectedSubtitle =
    selectedView === 'scatter'
      ? scatterSubtitle || `Month-end returns plotted against ${levelLabel.toLowerCase()} with a fitted regression line.`
      : subtitle;

  return (
    <Box className="regime-chart">
      <div className="regime-chart__controls">
        <div className="regime-chart__control-slot regime-chart__control-slot--left">
          <ToggleButtonGroup
            value={selectedType}
            exclusive
            onChange={handleTypeChange}
            aria-label={`${metricKey} return type`}
            size="small"
          >
            <ToggleButton value="Absolute" aria-label="Absolute">
              Absolute
            </ToggleButton>
            <ToggleButton value="Active" aria-label="Active">
              Active
            </ToggleButton>
          </ToggleButtonGroup>
        </div>

        <div className="regime-chart__control-slot regime-chart__control-slot--center">
          {middleControl}
        </div>

        <div className="regime-chart__control-slot regime-chart__control-slot--right">
          <ToggleButtonGroup
            value={selectedView}
            exclusive
            onChange={handleViewChange}
            aria-label={`${metricKey} chart view`}
            size="small"
          >
            <ToggleButton value="regimes" aria-label="Regimes">
              Regimes
            </ToggleButton>
            <ToggleButton value="scatter" aria-label="Scatterplot">
              Scatterplot
            </ToggleButton>
          </ToggleButtonGroup>
        </div>
      </div>

      {!hasAlignedData ? (
        <Alert severity="info">{emptyMessage}</Alert>
      ) : (
        <>
          {selectedView === 'scatter' ? (
            hasScatterData ? (
              <>
                <RegimeScatterView
                  scatterRows={scatterRows}
                  regressionSegment={regressionSegment}
                  config={config}
                  levelLabel={levelLabel}
                />
              </>
            ) : (
              <Alert severity="info">
                Scatterplot data is unavailable for this analysis. Re-run the analysis to generate the regression view.
              </Alert>
            )
          ) : (
            <RegimeBarView
              chartRows={chartRows}
              config={config}
              confidenceLevel={regimePayload?.confidence_level}
            />
          )}

          <div className="regime-chart__meta">
            <Typography variant="body2" className="regime-chart__meta-item">
              Aligned monthly observations: {regimePayload?.aligned_observation_count ?? 0}
            </Typography>
            <Typography
              variant="body2"
              className="regime-chart__meta-item regime-chart__meta-item--description"
            >
              {selectedSubtitle}
            </Typography>
          </div>
        </>
      )}
    </Box>
  );
};

export default RegimeChart;
